import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Table } from "react-bootstrap";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/UTDT_Logo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const CsAndItTable7 = ({ data }) => {
  const pdfRef = useRef();

  if (!data || data.length === 0) return <p>No data available</p>;

  const ageGroups = data.map((item) => item.age_group_label);
  const growthRates = data.map((item) => parseFloat(item.growth_rate));

  // Positive / Negative split visual effect
  const positiveRates = growthRates.map((v) => (v >= 0 ? v : null));
  const negativeRates = growthRates.map((v) => (v < 0 ? v : null));

  // Insights
  const highestGrowth = Math.max(...growthRates);
  const highestGrowthGroup = ageGroups[growthRates.indexOf(highestGrowth)];

  const lowestGrowth = Math.min(...growthRates);
  const lowestGrowthGroup = ageGroups[growthRates.indexOf(lowestGrowth)];

  const avgGrowth = (
    growthRates.reduce((a, b) => a + b, 0) / growthRates.length
  ).toFixed(1);

  const chartData = {
    labels: ageGroups,
    datasets: [
      {
        label: "Positive Growth",
        data: positiveRates,
        backgroundColor: "#999933",
        borderColor: "#999933",
        borderWidth: 1,
      },
      {
        label: "Negative Growth",
        data: negativeRates,
        backgroundColor: "red",
        borderColor: "red",
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📈 Employment Growth (%) by Age Group (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Age Group",
          font: { size: 14, weight: "bold" },
        },
      },
      y: {
        beginAtZero: false,
        title: {
          display: true,
          text: "Growth Rate (%)",
          font: { size: 14, weight: "bold" },
        },
        ticks: { callback: (v) => `${v}%` },
      },
    },
  };

  // Download PDF
  const downloadPDF = () => {
    const input = pdfRef.current;
    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      const addHeader = () => {
        const logoWidth = 20; // ideal header width
        const logoHeight = 20; // adjust ratio if needed

        pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY,
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right",
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // Add more pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Final footer
      // ===== FINAL FOOTER SAFE AREA =====

      const footerTopY = pageHeight - 40; // start footer 40mm from bottom
      const footerContentY = pageHeight - 28; // logos row
      const footerBottomTextY = pageHeight - 10; // bottom text row

      // Top Divider Line
      pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

      // UtahData logo
      const utahWidth = 18;
      const utahHeight = 18;
      pdf.addImage(
        utahData,
        "PNG",
        20,
        footerContentY - 8,
        utahWidth,
        utahHeight,
      );

      // Built by
      pdf.setFontSize(10);
      pdf.text("Built by", 45, footerContentY);

      // BODEX logo
      const bodexWidth = 36;
      const bodexHeight = bodexWidth * 0.3;
      pdf.addImage(
        bodex,
        "PNG",
        60,
        footerContentY - 6,
        bodexWidth,
        bodexHeight,
      );

      // Using text
      pdf.text("using", 102, footerContentY);

      // KPI logo
      pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

      // Version
      pdf.text("20250916.v3.198", 140, footerContentY);

      // Bottom Info Line
      pdf.setFontSize(8);
      pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
      pdf.text(
        `Downloaded on: ${new Date().toLocaleDateString()}`,
        pageWidth - 10,
        footerBottomTextY,
        null,
        null,
        "right",
      );

      pdf.save("cs_it_growth_age_2024.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-md">
        {/* Header */}
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
          Employment Growth (%) by Age Group (2018–2024)
        </h2>

        {/* Analytics Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Highest Growth</p>
            <p className="text-xl font-bold text-blue-900">
              {highestGrowthGroup} ({highestGrowth}%)
            </p>
          </div>

          <div className="p-4 bg-red-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Lowest Growth</p>
            <p className="text-xl font-bold text-red-900">
              {lowestGrowthGroup} ({lowestGrowth}%)
            </p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Average Growth</p>
            <p className="text-xl font-bold text-green-900">{avgGrowth}%</p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {/* TABLE */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-left">Age Group</th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      2018
                    </th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      2019
                    </th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      2020
                    </th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      2021
                    </th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      2022
                    </th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      2023
                    </th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      2024
                    </th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      Growth Rate (%)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-100 transition p-2">
                      <td className="p-2 text-left">{row.age_group_label}</td>
                      <td
                        className="p-2"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {Number(row.y2018).toLocaleString("en-US")}
                      </td>
                      <td
                        className="p-2"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {Number(row.y2019).toLocaleString("en-US")}
                      </td>
                      <td
                        className="p-2"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {Number(row.y2020).toLocaleString("en-US")}
                      </td>
                      <td
                        className="p-2"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {Number(row.y2021).toLocaleString("en-US")}
                      </td>
                      <td
                        className="p-2"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {Number(row.y2022).toLocaleString("en-US")}
                      </td>
                      <td
                        className="p-2"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {Number(row.y2023).toLocaleString("en-US")}
                      </td>
                      <td
                        className="p-2"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {Number(row.y2024).toLocaleString("en-US")}
                      </td>
                      <td
                        className="font-bold p-2"
                        style={{
                          color: row.growth_rate >= 0 ? "#0142B2" : "#CB0C0C",
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {row.growth_rate}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* CHART */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 text-white px-4 py-2 rounded-lg flex items-center gap-2 shadow-lg hover:scale-105 transition"
        >
          Download as PDF
          <img src={pdfIcon} alt="pdf" className="w-[1.5rem]" />
        </button>
      </div>
    </>
  );
};

export default CsAndItTable7;
